import React, {FC, useMemo, useRef} from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import annotationPlugin from 'chartjs-plugin-annotation';
import { Line } from 'react-chartjs-2';
import {Position} from "../../models/Position";

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    zoomPlugin,
    annotationPlugin
);

interface PositionsGraphAnalyserProps {
    positions: Position[];
    futurePrice: number;
    onClose: () => void;
}

const PositionsGraphAnalyser: FC<PositionsGraphAnalyserProps> = ({ positions, futurePrice, onClose }) => {
    const chartRef = useRef<any>(null);

    // Calculate net position at each price point
    const graphData = useMemo(() => {
        // Extract all strikes and sort them
        const strikes = positions
            .filter(p => p.strike > 0) // Filter out futures (strike = 0)
            .map(p => p.strike)
            .sort((a, b) => a - b);

        if (strikes.length === 0) {
            return { dataPoints: [], strikes: [], rangeMin: 0, rangeMax: 0 };
        }

        const minStrike = Math.min(...strikes);
        const maxStrike = Math.max(...strikes);

        // Calculate range with 1% buffer and ensure futurePrice is included
        const rangeMin = Math.min(minStrike * 0.99, futurePrice * 0.99);
        const rangeMax = Math.max(maxStrike * 1.01, futurePrice * 1.01);

        // Calculate net position at each price point
        const calculateNetPosition = (price: number): number => {
            let netPos = 0;

            positions.forEach(pos => {
                if (pos.type === 'FUT') {
                    // Futures contribute their quantity regardless of price
                    netPos += pos.quantity;
                } else if (pos.type === 'CE') {
                    // CE is ITM when price > strike (strictly)
                    if (price > pos.strike) {
                        netPos += pos.quantity;
                    }
                } else if (pos.type === 'PE') {
                    // PE is ITM when price < strike (strictly)
                    // PE gives opposite position: long put = short underlying
                    if (price < pos.strike) {
                        netPos -= pos.quantity;
                    }
                }
            });

            return netPos;
        };

        // Generate data points with step changes at each strike
        const dataPoints: Array<{price: number, netPos: number, isStrike: boolean}> = [];

        // Get unique strikes sorted
        const uniqueStrikes = [...new Set(strikes)].sort((a, b) => a - b);

        // Build segments between strikes
        const pricePoints = [rangeMin, ...uniqueStrikes, rangeMax];

        for (let i = 0; i < pricePoints.length - 1; i++) {
            const start = pricePoints[i];
            const end = pricePoints[i + 1];

            // Add point at start of segment (or just after if it's a strike)
            const startPrice = (i === 0) ? start : start + 0.01;
            dataPoints.push({
                price: startPrice,
                netPos: calculateNetPosition(startPrice),
                isStrike: i === 0 // Mark first point as displayable
            });

            // Add point just before end (to maintain horizontal line)
            if (i < pricePoints.length - 2) {
                // Not the last segment - add point just before next strike
                dataPoints.push({
                    price: end - 0.01,
                    netPos: calculateNetPosition(end - 0.01),
                    isStrike: false
                });
            } else {
                // Last segment - add end point
                dataPoints.push({
                    price: end,
                    netPos: calculateNetPosition(end),
                    isStrike: true
                });
            }
        }

        // Add strike points for labels
        uniqueStrikes.forEach(strike => {
            if (!dataPoints.find(p => Math.abs(p.price - strike) < 0.1)) {
                dataPoints.push({
                    price: strike,
                    netPos: 0,
                    isStrike: true
                });
            }
        });

        // Add current future price as an explicit data point
        if (!dataPoints.find(p => Math.abs(p.price - futurePrice) < 0.1)) {
            dataPoints.push({
                price: futurePrice,
                netPos: calculateNetPosition(futurePrice),
                isStrike: false
            });
        }

        // Sort all data points by price
        dataPoints.sort((a, b) => a.price - b.price);

        return {
            dataPoints,
            strikes: uniqueStrikes,
            rangeMin,
            rangeMax
        };
    }, [positions, futurePrice]);

    const handleZoomIn = () => {
        if (chartRef.current) {
            chartRef.current.zoom(1.1);
        }
    };

    const handleZoomOut = () => {
        if (chartRef.current) {
            chartRef.current.zoom(0.9);
        }
    };

    const handleResetZoom = () => {
        if (chartRef.current) {
            chartRef.current.resetZoom();
        }
    };

    const chartData = {
        labels: graphData.dataPoints.map(p => p.price),
        datasets: [
            {
                label: 'Net Position',
                data: graphData.dataPoints.map(p => p.netPos),
                borderColor: 'rgb(53, 162, 235)',
                backgroundColor: 'rgba(53, 162, 235, 0.5)',
                tension: 0, // Straight lines between points
                pointRadius: 0, // Hide points by default
                pointHoverRadius: 6, // Show larger point on hover
                pointHitRadius: 10, // Larger hit area for easier hovering
                borderWidth: 2,
                hoverBorderWidth: 3,
            },
        ],
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            mode: 'nearest' as const,
            axis: 'x' as const,
            intersect: false
        },
        plugins: {
            legend: {
                position: 'top' as const,
            },
            title: {
                display: true,
                text: 'Net Position vs Future Price Analysis',
            },
            tooltip: {
                enabled: true,
                mode: 'nearest' as const,
                axis: 'x' as const,
                intersect: false,
                callbacks: {
                    title: function(context: any) {
                        // Get the actual price from parsed.x (linear scale)
                        const price = context[0].parsed.x;
                        return `Price: ${price.toFixed(2)}`;
                    },
                    label: function(context: any) {
                        return `Net Position: ${context.parsed.y}`;
                    }
                }
            },
            annotation: {
                annotations: {
                    currentPriceLine: {
                        type: 'line' as const,
                        xMin: futurePrice,
                        xMax: futurePrice,
                        borderColor: 'rgb(255, 0, 0)',
                        borderWidth: 2,
                        borderDash: [5, 5],
                        label: {
                            display: true,
                            content: `Current: ${futurePrice.toFixed(0)}`,
                            position: 'start' as const,
                            backgroundColor: 'rgb(255, 0, 0)',
                            color: 'white',
                            font: {
                                size: 11,
                                weight: 'bold' as const
                            }
                        }
                    }
                }
            },
            zoom: {
                zoom: {
                    wheel: {
                        enabled: true,
                    },
                    pinch: {
                        enabled: true
                    },
                    mode: 'x' as const,
                },
                pan: {
                    enabled: true,
                    mode: 'x' as const,
                    modifierKey: undefined, // Allow panning without holding any key
                },
                limits: {
                    x: {
                        min: 'original' as const,
                        max: 'original' as const,
                        minRange: 100
                    }
                }
            }
        },
        scales: {
            x: {
                type: 'linear' as const,
                // Remove fixed min/max to allow zooming
                // min: graphData.rangeMin,
                // max: graphData.rangeMax,
                title: {
                    display: true,
                    text: 'Closing Price of Underlying Future'
                }
            },
            y: {
                title: {
                    display: true,
                    text: 'Net Position'
                }
            }
        }
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>Position Graph Analysis</h2>
                    <div style={{ marginLeft: 'auto', marginRight: '20px' }}>
                        <button onClick={handleZoomOut}>-</button>
                        <span style={{ margin: '0 10px' }}>Zoom</span>
                        <button onClick={handleZoomIn}>+</button>
                        <button onClick={handleResetZoom} style={{ marginLeft: '10px' }}>Reset</button>
                    </div>
                    <button className="close-btn" onClick={onClose}>×</button>
                </div>
                <div className="modal-body">
                    <div style={{ height: '70vh', width: '100%' }}>
                        <Line ref={chartRef} options={options} data={chartData} />
                    </div>
                    <div className="graph-info">
                        <p><b>Current Future Price:</b> {futurePrice.toFixed(2)}</p>
                        <p><b>Current Net Position:</b> {(() => {
                            let netPos = 0;
                            positions.forEach(pos => {
                                if (pos.type === 'FUT') {
                                    netPos += pos.quantity;
                                } else if (pos.type === 'CE') {
                                    if (futurePrice > pos.strike) {
                                        netPos += pos.quantity;
                                    }
                                } else if (pos.type === 'PE') {
                                    if (futurePrice < pos.strike) {
                                        netPos -= pos.quantity;
                                    }
                                }
                            });
                            return netPos;
                        })()}</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PositionsGraphAnalyser;
